import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { local } from 'embroider-css-modules';
import HotelChannelPricingTablesCellsPmsTableTdPmsTableNameCell from 'lumen/components/hotel/channel-pricing/tables/cells/pms-table/td-pms-table-name-cell';
import HotelChannelPricingTablesCellsPmsTableTdPmsTableValueCell from 'lumen/components/hotel/channel-pricing/tables/cells/pms-table/td-pms-table-value-cell';
import type {
  Carate,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  PMS_TABLE_ROW_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
} from 'lumen/utils/constants/channel-pricing-constants';
import { DAY_TABLE_SIZES } from 'lumen/utils/constants/general-constants';

import styles from './pms-table.module.css';

const ROW_PROPERTIES = {
  NAME: 'name',
  TYPE: 'type',
  PMS_ROOMTYPE: 'pmsRoomtype',
} as const;

export interface TdPmsTablePeriodColumnSignature extends CustomEmberTableColumn {
  daysWithActivePricePush: string[];
  daysWithFailedPricePush: string[];
  daysWithFailedRestrictionPush: string[];
  id: string;
  isPastDay: boolean;
  restrictions: Restriction[];
  restrictionsRecommendations: RestrictionRecommendation[];
}

export interface TdPmsTableRowSignature extends CustomEmberTableRow {
  carates?: Carate[];
  /** Carates grouped by dates. Currently used to detect inconsistent rates per day (across multiple rooms) */
  caratesPerPeriod?: Record<string, Carate[]>;
  children?: TdPmsTableRowSignature[];
  [ROW_PROPERTIES.NAME]: string;
  [ROW_PROPERTIES.TYPE]: (typeof PMS_TABLE_ROW_TYPES)[keyof typeof PMS_TABLE_ROW_TYPES];
  [ROW_PROPERTIES.PMS_ROOMTYPE]?: string;
}

interface HotelChannelPricingTablesPmsTableSignature {
  Args: {
    caratesData: Carate[];
    daysWithActivePricePush: string[];
    daysWithFailedPricePush: string[];
    daysWithFailedRestrictionPush: string[];
    isLoading: boolean;
    periodsData: Period[];
  };
}

export default class HotelChannelPricingTablesPmsTable extends Component<HotelChannelPricingTablesPmsTableSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isPricingBaseRoomRowCollapsed = true;

  get caratesPerPeriod(): Record<string, Carate[]> {
    const result = this.args.caratesData.reduce(
      (accumulator: Record<string, Carate[]>, carate) => {
        const day = carate.period.day.id;
        if (!day) {
          return accumulator;
        }

        if (!accumulator[day]) {
          accumulator[day] = [];
        }

        accumulator[day].push(carate);
        return accumulator;
      },
      {} as Record<string, Carate[]>,
    );

    return result;
  }

  get caratesPerPmsRoomtype(): Record<string, Carate[]> {
    const result = this.args.caratesData.reduce(
      (accumulator: Record<string, Carate[]>, carate) => {
        const { pmsRoomtype } = carate;
        if (!accumulator[pmsRoomtype]) {
          accumulator[pmsRoomtype] = [];
        }
        accumulator[pmsRoomtype].push(carate);
        return accumulator;
      },
      {},
    );

    return result;
  }

  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('channel_pricing.restrictions'),
        valuePath: 'name',
        textAlign: 'left',
        maxWidth: DAY_TABLE_SIZES.NAME_MAX_WIDTH,
        tdClassNames:
          'ic-product-tour-49521458-sd-pms-table-name-cell ic-product-tour-55124760-sd-pms-table-name-cell',
        tdComponent: HotelChannelPricingTablesCellsPmsTableTdPmsTableNameCell,
      },
    ];

    this.args.periodsData.forEach((period) => {
      const restrictions = period.restrictions.filter((restriction) => {
        return restriction.context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL;
      });

      const hotelLevelRestrictionRecommendations: RestrictionRecommendation[] =
        period.restrictionRecommendations.filter(
          (restrictionRecommendation) =>
            restrictionRecommendation.context ===
            RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        );

      columns.push({
        id: period.day.id,
        name: period.day.id!,
        restrictions,
        restrictionsRecommendations: hotelLevelRestrictionRecommendations,
        valuePath: '',
        width: DAY_TABLE_SIZES.VALUE_WIDTH,
        minWidth: DAY_TABLE_SIZES.VALUE_MIN_WIDTH,
        tdComponent: HotelChannelPricingTablesCellsPmsTableTdPmsTableValueCell,
        isPastDay: period.day.isPast,
        tdClassNames: 'channel-pricing-td-no-default-padding',
        daysWithActivePricePush: this.args.daysWithActivePricePush,
        daysWithFailedPricePush: this.args.daysWithFailedPricePush,
        daysWithFailedRestrictionPush: this.args.daysWithFailedRestrictionPush,
      });
    });

    return columns;
  }

  get rows(): TdPmsTableRowSignature[] {
    const pricingRow: Partial<TdPmsTableRowSignature> = {
      [ROW_PROPERTIES.TYPE]: PMS_TABLE_ROW_TYPES.PRICING_PARENT_ROW,
      children: [],
      isCollapsed: this.isPricingBaseRoomRowCollapsed,
    };

    Object.keys(this.caratesPerPmsRoomtype).forEach((pmsRoomtype) => {
      let formattedPmsRoomtype = pmsRoomtype;

      const pmsRoomtypeIdToPmsNameMapping =
        this.hotelAndMarketService.hotel.pricingIntegration
          .pms_id_to_pms_room_name;

      if (pmsRoomtypeIdToPmsNameMapping[pmsRoomtype]) {
        formattedPmsRoomtype = pmsRoomtypeIdToPmsNameMapping[pmsRoomtype];
      }

      const isBaseroom =
        this.hotelAndMarketService.hotel.pricingIntegration
          .pms_leading_room_pms_id === pmsRoomtype;

      const carates = this.caratesPerPmsRoomtype[pmsRoomtype] || [];

      if (isBaseroom) {
        pricingRow[ROW_PROPERTIES.NAME] = formattedPmsRoomtype;
        pricingRow[ROW_PROPERTIES.PMS_ROOMTYPE] = pmsRoomtype;
        pricingRow['carates'] = carates;
        pricingRow['caratesPerPeriod'] = this.caratesPerPeriod || [];
      } else if (pricingRow['children']) {
        pricingRow['children'].push({
          [ROW_PROPERTIES.NAME]: formattedPmsRoomtype,
          [ROW_PROPERTIES.TYPE]: PMS_TABLE_ROW_TYPES.PRICING_CHILD_ROW,
          [ROW_PROPERTIES.PMS_ROOMTYPE]: pmsRoomtype,
          carates,
        });
      }
    });

    const rows: TdPmsTableRowSignature[] = [];

    if (this.access.hasHotelRestrictionSupport) {
      rows.push({
        [ROW_PROPERTIES.NAME]: `${this.intl.t('channel_pricing.restrictions')} (${this.intl.t('general.hotel.plural', { count: 1 })})`,
        [ROW_PROPERTIES.TYPE]: PMS_TABLE_ROW_TYPES.RESTRICTION,
      });
    }

    rows.push(pricingRow as TdPmsTableRowSignature);

    return rows;
  }

  @action
  onRowCollapseToggle(row: CustomEmberTableRow): void {
    if (row['type'] === PMS_TABLE_ROW_TYPES.PRICING_PARENT_ROW) {
      this.isPricingBaseRoomRowCollapsed = !this.isPricingBaseRoomRowCollapsed;

      if (!this.isPricingBaseRoomRowCollapsed) {
        this.usageAnalytics.track('Room Type Previewed', {
          eventTrigger: 'pms-table',
          product: 'smart-distribution',
        });
      }
    }
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @fillMode="first-column"
      @isLoading={{@isLoading}}
      @nrOfEmptyRows={{0}}
      @nrOfLoadingRows={{2}}
      @onRowCollapseToggle={{this.onRowCollapseToggle}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
      class="{{local
          styles
          'table'
          'pms-table'
          (if @isLoading 'pms-table-loading')
        }}
        ic-product-tour-49521458-sd-pms-table"
      data-test-id="pms-table"
      {{! SMART DISTRIBUTION TODO: nrOfEmptyRows 0 is actually ignored in the Prism::Table. Can be investigated/confirmed later }}
    />
  </template>
}
